import React, { useState, useEffect } from "react";
import { Card, ListGroup } from "react-bootstrap";
import styles from "./Card.module.css";
import GenericFHIRViewerModal from "../modals/ResourceViewerModal";
import { formatLocalDate } from "../utils/dateUtils";
import { useStore } from "react-redux";
import QuestionnaireIcon from "../assets/question-1.png"; // replace if needed
import { resolveName } from "../utils/messageUtils";

function QuestionnaireResponseCard({
    title,
    inContext,
    qrData,
    fhirClient,
    isReply,
    depth = 6,
}) {
    const [error, setError] = useState("");
    const [showModal, setShowModal] = useState(false);
    const [modalQRDetails, setModalQRDetails] = useState(null);
    const [selectedQR, setSelectedQR] = useState(qrData || null);

    const store = useStore();
    const handleModalClose = () => setShowModal(false);

    if (!title) {
        title = resolveName(qrData, fhirClient, store);
    }

    /** Sync incoming QR */
    useEffect(() => {
        if (qrData?.error) {
            setError(
                `Error loading QuestionnaireResponse (code: ${qrData.error}).`
            );
            setSelectedQR(null);
        } else {
            setError("");
            setSelectedQR(qrData || null);
        }
    }, [qrData]);

    /** Open modal with fully loaded resource */
    const handleModalOpen = async () => {
        if (!selectedQR?.id) return;

        try {
            const details = await fhirClient.request(
                `QuestionnaireResponse/${selectedQR.id}`
            );
            setModalQRDetails(details);
        } catch (e) {
            console.warn("Failed to load QuestionnaireResponse details", e);
            setModalQRDetails(null);
        }
        setShowModal(true);
    };

    /** Field rendering */
    const fieldRenderers = [
        <ListGroup.Item className={styles.listItem} key="status">
            <strong>Status:</strong> {selectedQR?.status || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="authored">
            <strong>Authored:</strong>{" "}
            {formatLocalDate(selectedQR?.authored)}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="subject">
            <strong>Subject:</strong>{" "}
            {selectedQR?.subject?.display ||
                selectedQR?.subject?.reference ||
                "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="author">
            <strong>Author:</strong>{" "}
            {selectedQR?.author?.display ||
                selectedQR?.author?.reference ||
                "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="questionnaire">
            <strong>Questionnaire:</strong>{" "}
            {selectedQR?.questionnaire || "N/A"}
        </ListGroup.Item>,

        <ListGroup.Item className={styles.listItem} key="itemCount">
            <strong>Items:</strong>{" "}
            {selectedQR?.item?.length ?? 0}
        </ListGroup.Item>,
    ];

    return (
        <>
            <Card className={styles.shrinkCard}>
                {/* HEADER */}
                <Card.Header
                    className={`d-flex justify-content-between align-items-center ${
                        inContext ? styles.inContextCardHeader : styles.cardHeader
                    }`}
                    onClick={handleModalOpen}
                    style={{ cursor: "pointer" }}
                >
                    {/* LEFT SIDE */}
                    <div
                        className="d-flex align-items-center"
                        style={{ pointerEvents: "none" }}
                    >
                        <img
                            src={QuestionnaireIcon}
                            alt="QuestionnaireResponse Icon"
                            className={styles.fhirIcon}
                        />

                        <h6 className="mb-0" style={{ marginLeft: "0.5rem" }}>
                            {title?.length > 75
                                ? `Questionnaire Response: ${title.substring(0, 75)}…`
                                : `Questionnaire Response: ${title}`}
                        </h6>
                    </div>
                </Card.Header>

                {/* BODY */}
                {depth > 0 && (
                    <Card.Body>
                        {error ? (
                            <p className={styles.errorText}>{error}</p>
                        ) : (
                            <ListGroup variant="flush">
                                {fieldRenderers.slice(0, depth)}
                            </ListGroup>
                        )}
                    </Card.Body>
                )}
            </Card>

            {/* MODAL VIEWER */}
            <GenericFHIRViewerModal
                show={showModal}
                onClose={handleModalClose}
                resource={modalQRDetails}
                fhirClient={fhirClient}
            />
        </>
    );
}

export default QuestionnaireResponseCard;
